import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from '../hooks/useAuth';
import { Company, CompanySettings } from '../types';

interface CompanyContextType {
  company: Company | null;
  settings: CompanySettings | null;
  loading: boolean;
  error: Error | null;
  refreshCompany: () => Promise<void>;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export const CompanyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, companyId, isAdmin, viewAsCompanyId, effectiveCompanyId } = useAuth();
  const [company, setCompany] = useState<Company | null>(null);
  const [settings, setSettings] = useState<CompanySettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const loadedCompanyIdRef = React.useRef<string | null>(null);

  const loadCompany = async () => {
    if (!user || (!effectiveCompanyId && !isAdmin)) {
      setCompany(null);
      setSettings(null);
      loadedCompanyIdRef.current = null;
      setLoading(false);
      return;
    }

    try {
      // Soft refresh: keep UI mounted when reloading the same tenant (e.g. tab token refresh)
      const sameTenantAlreadyLoaded =
        !!effectiveCompanyId && loadedCompanyIdRef.current === effectiveCompanyId;
      if (!sameTenantAlreadyLoaded) {
        setLoading(true);
      }
      setError(null);

      // Admin with neither membership nor View as → no tenant chrome
      if (!effectiveCompanyId) {
        setCompany(null);
        setSettings(null);
        loadedCompanyIdRef.current = null;
        setLoading(false);
        return;
      }

      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('*')
        .eq('id', effectiveCompanyId)
        .single();

      if (companyError) throw companyError;
      setCompany(companyData as Company);
      loadedCompanyIdRef.current = effectiveCompanyId;

      const { data: settingsData, error: settingsError } = await supabase
        .from('company_settings')
        .select('*')
        .eq('company_id', effectiveCompanyId)
        .single();

      if (settingsError && settingsError.code !== 'PGRST116') {
        // PGRST116 = no rows returned
        console.error('Error loading company settings:', settingsError);
      } else if (settingsData) {
        setSettings(settingsData as CompanySettings);
      } else {
        const { data: newSettings } = await supabase
          .from('company_settings')
          .insert({
            company_id: effectiveCompanyId,
            theme_config: {},
            content_config: {},
            features: {},
            customizations: {},
          })
          .select()
          .single();

        if (newSettings) {
          setSettings(newSettings as CompanySettings);
        }
      }
    } catch (err) {
      const nextError = err instanceof Error ? err : new Error('Failed to load company');
      setError(nextError);
      console.error('Error loading company:', nextError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCompany();
    // Reload when membership or View as changes (stable user id, not user object)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, companyId, isAdmin, viewAsCompanyId, effectiveCompanyId]);

  const refreshCompany = async () => {
    await loadCompany();
  };

  return (
    <CompanyContext.Provider
      value={{
        company,
        settings,
        loading,
        error,
        refreshCompany,
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
};

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};
